/**
 * Per-page SEO meta management for the SPA.
 *
 * The site is client-side rendered, so there is no server-rendered <title> or
 * <meta description>. These helpers update the document head at runtime — call
 * setPageMeta() from a route's component once its data has loaded, so each URL
 * gets a unique title, description, canonical and social tags that Google reads
 * when it renders the page.
 *
 * No external dependency (no vue-meta) — just DOM head manipulation.
 */

const SITE_NAME = "Just Cuts";

const DEFAULT_DESCRIPTION =
    "Just Cuts is the largest hair salon brand in the Southern Hemisphere — no-appointment, walk-in Style Cuts. Find a salon, explore careers and franchising opportunities.";

// Strip HTML tags and collapse whitespace so rich-text fields can be reused as
// plain-text meta descriptions.
const stripHtml = (value) => String(value == null ? "" : value)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const truncate = (text, length = 160) => {
    const clean = stripHtml(text);
    if (clean.length <= length) return clean;
    return clean.slice(0, length - 1).replace(/\s+\S*$/, "").trim() + "…";
};

const upsertMeta = (attr, key, content) => {
    if (content == null || content === "") return;
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
};

const upsertCanonical = (href) => {
    let el = document.head.querySelector('link[rel="canonical"]');
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", "canonical");
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
};

const setRobots = (content) => upsertMeta("name", "robots", content);

/**
 * Set the document head for the current page.
 *
 * @param {Object}  opts
 * @param {string} [opts.title]        Page title (brand name is appended automatically).
 * @param {string} [opts.description]  Description; HTML is stripped and truncated to ~160 chars.
 * @param {string} [opts.image]        Absolute URL for social share image (og:image).
 * @param {string} [opts.path]         Path for canonical/og:url (defaults to current location).
 * @param {string} [opts.type]         Open Graph type (default "website"; use "article" for news).
 * @param {boolean}[opts.noindex]      When true, adds <meta name="robots" content="noindex">.
 */
export function setPageMeta({ title, description, image, path, type = "website", noindex = false } = {}) {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
    const desc = truncate(description) || DEFAULT_DESCRIPTION;
    const origin = window.location.origin;
    const url = origin + (path || window.location.pathname);

    document.title = fullTitle;

    upsertMeta("name", "description", desc);

    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:site_name", SITE_NAME);
    if (image) upsertMeta("property", "og:image", image);

    upsertMeta("name", "twitter:card", image ? "summary_large_image" : "summary");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", desc);
    if (image) upsertMeta("name", "twitter:image", image);

    upsertCanonical(url);

    // Index by default; only opt out when explicitly told to (e.g. 404s).
    setRobots(noindex ? "noindex, follow" : "index, follow");
}

export { SITE_NAME, DEFAULT_DESCRIPTION };
